import { ReactNode, useRef } from 'react';
import * as RadixTabs from '@radix-ui/react-tabs';
import { cn } from '@new/lib';

export type TabItem<T extends string = string> = {
  value: T;
  label: string;
  count?: number;
  content?: ReactNode;
  disableScroll?: boolean;
  keepMounted?: boolean;
};

type TabsProps<T extends string = string> = {
  tabs: TabItem<T>[];
  defaultValue?: T;
  value?: T;
  onValueChange?: (value: T) => void;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
};

export const Tabs = <T extends string = string>({
  tabs,
  defaultValue,
  value,
  onValueChange,
  className,
  triggerClassName,
  contentClassName,
}: TabsProps<T>) => {
  const listRef = useRef<HTMLDivElement>(null);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!listRef.current) return;
    listRef.current.scrollLeft += e.deltaY + e.deltaX;
  };

  return (
    <RadixTabs.Root
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange as (value: string) => void}
      className={cn('w-full flex flex-col h-full', className)}
    >
      <RadixTabs.List
        ref={listRef}
        onWheel={handleWheel}
        className="flex flex-wrap overflow-x-auto w-full shrink-0 scrollbar-none border-b border-b-(--neutral-black-NB100)"
      >
        {tabs.map((tab) => (
          <RadixTabs.Trigger
            key={tab.value}
            value={tab.value}
            className={cn(
              'flex flex-1 sm:flex-none h-11.5 px-6 py-2.5 justify-center items-center whitespace-nowrap transition-colors border-b-2! border-b-(--neutral-black-NB100)!',
              'border-b-4! border-b-transparent!',
              'data-[state=active]:border-b-(--primary-O900)!',
              'hover:border-b-[#FE870150]!',
              'focus:outline-none',
              'cursor-pointer!',
              triggerClassName
            )}
          >
            <span
              className={cn(
                '[font-family:var(--font-arvo)] [font-size:var(--fs-p4)] lg:[font-size:var(--fs-p3)] font-bold',
                'text-(--neutral-black-NB700)'
              )}
            >
              {tab.label}
              {tab.count !== undefined && ` (${tab.count})`}
            </span>
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>

      {tabs.map((tab) =>
        tab.content ? (
          <RadixTabs.Content
            key={tab.value}
            value={tab.value}
            forceMount={tab.keepMounted}
            className={cn(
              'mt-4',
              !tab.disableScroll && 'overflow-y-auto',
              tab.keepMounted && 'data-[state=inactive]:hidden',
              contentClassName
            )}
          >
            {tab.content}
          </RadixTabs.Content>
        ) : null
      )}
    </RadixTabs.Root>
  );
};
